// src/components/proposal/DeliverablesSectionContent.tsx
// Main component for the Deliverables tab in ProposalDraftModal

import React, { useState } from 'react';
import {
  Package,
  Clock,
  Users,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Pencil,
  Plus,
  Trash2,
  Save,
  X,
} from 'lucide-react';
import { DeliverableCard } from './DeliverableCard';
import { LaborSummaryTable } from './LaborSummaryTable';
import { AiPricingPanel, type PricingAnswers } from './AiPricingPanel';
import { supabase } from '../../lib/supabase';
import { ComplianceChecklist } from './ComplianceChecklist';
import type { DeliverablesData, DeliverableWithLabor, AiPricing } from '../../types/deliverables';

interface DeliverablesSectionContentProps {
  deliverables?: DeliverablesData;
  onEnhanceSection?: (deliverableId: string) => void;
  onEnhanceDeliverables?: () => void;
  onRegenerateDeliverables?: () => void;
  /** Persist edited deliverables (inline structured editor). */
  onSaveDeliverables?: (updated: DeliverablesData) => Promise<void> | void;
  isSaving?: boolean;
  isEnhancing?: boolean;
  enhancingId?: string;
  isRegenerating?: boolean;
  isEnhancingSection?: boolean;
  className?: string;
}

const DELIVERABLE_TYPES = ['document', 'report', 'software', 'training', 'service', 'product', 'other'];

// Sum per-deliverable hours into a {min,max,recommended} total.
function sumDeliverableHours(items: any[]): { min: number; max: number; recommended: number } {
  return items.reduce(
    (acc, d) => ({
      min: acc.min + (Number(d?.total_hours?.min) || 0),
      max: acc.max + (Number(d?.total_hours?.max) || 0),
      recommended: acc.recommended + (Number(d?.total_hours?.recommended) || 0),
    }),
    { min: 0, max: 0, recommended: 0 }
  );
}

export function DeliverablesSectionContent({
  deliverables,
  onEnhanceSection,
  onEnhanceDeliverables,
  onRegenerateDeliverables,
  onSaveDeliverables,
  isSaving = false,
  isEnhancing = false,
  enhancingId,
  isRegenerating = false,
  isEnhancingSection = false,
  className = '',
}: DeliverablesSectionContentProps) {
  const [showAllDeliverables, setShowAllDeliverables] = useState(false);
  // Inline structured editor state.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any[]>([]);

  const startEditing = () => {
    setDraft(JSON.parse(JSON.stringify(deliverables?.deliverables_with_labor || [])));
    setEditing(true);
  };
  const cancelEditing = () => { setEditing(false); setDraft([]); };

  const updateDraft = (idx: number, patch: any) => {
    setDraft((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };
  const updateDraftHours = (idx: number, key: 'min' | 'max' | 'recommended', value: number) => {
    setDraft((prev) => prev.map((d, i) => (i === idx ? { ...d, total_hours: { ...(d.total_hours || {}), [key]: value } } : d)));
  };
  const addDraftDeliverable = () => {
    setDraft((prev) => [
      ...prev,
      {
        deliverable_id: `D${String(prev.length + 1).padStart(3, '0')}-${Date.now().toString(36)}`,
        deliverable_name: 'New deliverable',
        description: '',
        type: 'document',
        total_hours: { min: 8, max: 24, recommended: 16 },
        labor_phases: [],
        compliance_requirements: [],
      },
    ]);
  };
  const removeDraftDeliverable = (idx: number) => setDraft((prev) => prev.filter((_, i) => i !== idx));

  const saveEditing = async () => {
    if (!onSaveDeliverables || !deliverables) return;
    const newTotals = sumDeliverableHours(draft);
    const oldRec = Number(deliverables.labor_summary?.total_hours?.recommended) || 0;
    const factor = oldRec > 0 ? newTotals.recommended / oldRec : 1;
    // Keep the labor-category table internally consistent by scaling it to the new total.
    const scaledByCategory = (deliverables.labor_summary?.by_category || []).map((c: any) => ({
      ...c,
      min_hours: Math.round((Number(c.min_hours) || 0) * factor),
      max_hours: Math.round((Number(c.max_hours) || 0) * factor),
    }));
    const updated: DeliverablesData = {
      ...deliverables,
      deliverables_with_labor: draft,
      labor_summary: {
        ...(deliverables.labor_summary as any),
        total_hours: { ...(deliverables.labor_summary?.total_hours as any), ...newTotals },
        by_category: scaledByCategory,
      },
    } as DeliverablesData;
    await onSaveDeliverables(updated);
    setEditing(false);
  };
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['summary', 'deliverables'])
  );
  // Local override so "Apply answers & re-price" updates the panel in place.
  const [overrideAiPricing, setOverrideAiPricing] = useState<AiPricing | null>(null);
  const [isRepricing, setIsRepricing] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Handle empty or missing data
  if (!deliverables || !deliverables.deliverables_with_labor || deliverables.deliverables_with_labor.length === 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-6 text-center">
          <Package className="h-12 w-12 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-fit-moderate mb-2">
            No Deliverables Data Available
          </h3>
          <p className="text-fit-moderate text-sm">
            Deliverables will be extracted and analyzed when the proposal is generated with
            attached RFQ documents. Upload your solicitation documents to enable deliverables
            analysis.
          </p>
        </div>
      </div>
    );
  }

  const {
    deliverables_with_labor,
    labor_summary,
    pricing_inputs,
    compliance_framework,
    timeline_summary,
    volume_summary,
    ai_pricing,
  } = deliverables;

  const effectiveAiPricing = overrideAiPricing || ai_pricing;

  // Re-derive pricing with the user's clarifying-question answers as
  // authoritative, anchored to the previously-derived base rates.
  const handleReprice = async (answers: PricingAnswers) => {
    const cats = (labor_summary?.by_category || []).map((c) => ({
      category: c.category,
      sca_title: c.sca_title,
      total_hours: c.total_hours,
    }));
    if (cats.length === 0) return;
    setIsRepricing(true);
    try {
      const known_rates = (effectiveAiPricing?.cost_rollup?.by_category || []).map((c) => ({
        category: c.category,
        base_hourly: c.base_hourly,
      }));
      const { data, error } = await supabase.functions.invoke('derive-labor-rates', {
        body: { labor_categories: cats, profile: { known_rates }, user_answers: answers },
      });
      if (!error && data?.cost_rollup) setOverrideAiPricing(data as AiPricing);
    } catch (e) {
      console.error('Re-price failed:', e);
    } finally {
      setIsRepricing(false);
    }
  };

  // Show first 5 deliverables by default, or all if expanded
  const visibleDeliverables = showAllDeliverables
    ? deliverables_with_labor
    : deliverables_with_labor.slice(0, 5);
  const hasMoreDeliverables = deliverables_with_labor.length > 5;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Enhance and Regenerate Buttons */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-bw-ink">
          Deliverables Analysis
        </h3>
        <div className="flex items-center gap-2">
          {onSaveDeliverables && !editing && (
            <button
              onClick={startEditing}
              disabled={isRegenerating || isEnhancingSection}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bw-ink bg-bw-surface-subtle hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <Pencil className="h-4 w-4" /> Edit
            </button>
          )}
          {onSaveDeliverables && editing && (
            <>
              <button
                onClick={saveEditing}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-bw-primary hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button
                onClick={cancelEditing}
                disabled={isSaving}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-bw-ink bg-bw-surface-subtle hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            </>
          )}
          {onEnhanceDeliverables && !editing && (
            <button
              onClick={onEnhanceDeliverables}
              disabled={isEnhancingSection || isRegenerating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bw-primary bg-bw-primary-tint hover:bg-bw-primary-tint rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEnhancingSection ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Enhance Section
                </>
              )}
            </button>
          )}
          {onRegenerateDeliverables && !editing && (
            <button
              onClick={onRegenerateDeliverables}
              disabled={isRegenerating || isEnhancingSection}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bw-primary bg-bw-primary-tint hover:bg-bw-primary-tint rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRegenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Regenerate Deliverables
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="bg-bw-surface-subtle rounded-xl p-5 border border-blue-100">
        <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Deliverables Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Deliverables */}
          <div className="bg-bw-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-bw-muted text-sm mb-1">
              <Package className="h-4 w-4" />
              Total Deliverables
            </div>
            <p className="text-2xl font-bold text-bw-ink">
              {deliverables_with_labor.length}
            </p>
            {volume_summary?.total_items && (
              <p className="text-xs text-bw-muted">
                {typeof volume_summary.total_items === 'number'
                  ? `${volume_summary.total_items} items`
                  : volume_summary.total_items}
              </p>
            )}
          </div>

          {/* Estimated Hours */}
          <div className="bg-bw-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-bw-muted text-sm mb-1">
              <Clock className="h-4 w-4" />
              Estimated Hours
            </div>
            <p className="text-2xl font-bold text-bw-primary">
              {labor_summary?.total_hours?.recommended?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-bw-muted">
              Range: {labor_summary?.total_hours?.min?.toLocaleString() || 0} -{' '}
              {labor_summary?.total_hours?.max?.toLocaleString() || 0}
            </p>
          </div>

          {/* Labor Categories */}
          <div className="bg-bw-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-bw-muted text-sm mb-1">
              <Users className="h-4 w-4" />
              Labor Categories
            </div>
            <p className="text-2xl font-bold text-bw-primary">
              {labor_summary?.by_category?.length || 0}
            </p>
            <p className="text-xs text-bw-muted">
              {pricing_inputs?.sca_wage_determination_needed ? 'SCA Required' : 'Standard Rates'}
            </p>
          </div>

          {/* Rate Basis */}
          <div className="bg-bw-surface rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-bw-muted text-sm mb-1">
              <DollarSign className="h-4 w-4" />
              Rate Basis
            </div>
            <p className="text-2xl font-bold text-success uppercase">
              {pricing_inputs?.suggested_rate_basis || 'Market'}
            </p>
            {labor_summary?.total_hours?.ceiling && (
              <p className="text-xs text-bw-muted">
                Ceiling: {labor_summary.total_hours.ceiling.toLocaleString()} hrs
              </p>
            )}
          </div>
        </div>

        {/* Warning if over ceiling */}
        {labor_summary?.total_hours?.ceiling &&
          labor_summary.total_hours.recommended > labor_summary.total_hours.ceiling && (
            <div className="mt-4 bg-urgent-bg border border-urgent-border rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-urgent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-urgent">Hours Exceed Ceiling</p>
                <p className="text-xs text-urgent">
                  Estimated hours ({labor_summary.total_hours.recommended.toLocaleString()}) exceed the
                  contract ceiling ({labor_summary.total_hours.ceiling.toLocaleString()} hrs) by{' '}
                  {(labor_summary.total_hours.recommended - labor_summary.total_hours.ceiling).toLocaleString()}{' '}
                  hours. Review deliverable scope or efficiency assumptions.
                </p>
              </div>
            </div>
          )}

        {/* Timeline Summary */}
        {timeline_summary && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="text-sm">
              <span className="font-medium text-bw-primary">Period of Performance:</span>{' '}
              <span className="text-bw-primary">{timeline_summary.period_of_performance}</span>
            </div>
            {timeline_summary.pace_requirements && (
              <div className="text-sm mt-1">
                <span className="font-medium text-bw-primary">Pace:</span>{' '}
                <span className="text-bw-primary">{timeline_summary.pace_requirements}</span>
              </div>
            )}
            {timeline_summary.key_milestones && timeline_summary.key_milestones.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {timeline_summary.key_milestones.slice(0, 4).map((milestone, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-1 bg-bw-primary-tint text-bw-primary rounded text-xs"
                  >
                    {milestone.date}: {milestone.milestone}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Deliverables List */}
      <div className="space-y-4">
        <button
          onClick={() => toggleSection('deliverables')}
          className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <Package className="h-5 w-5 text-slate-600" />
            Deliverables ({deliverables_with_labor.length})
          </h3>
          {expandedSections.has('deliverables') ? (
            <ChevronUp className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          )}
        </button>

        {expandedSections.has('deliverables') && editing && (
          <div className="space-y-3">
            {draft.map((d, index) => (
              <div key={d.deliverable_id || index} className="bg-bw-surface border border-bw-border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-xs font-mono text-bw-muted mt-2 shrink-0">{String(index + 1).padStart(2, '0')}</span>
                  <div className="flex-1 space-y-2">
                    <input
                      value={d.deliverable_name || ''}
                      onChange={(e) => updateDraft(index, { deliverable_name: e.target.value })}
                      placeholder="Deliverable name"
                      className="w-full text-sm font-medium rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary px-2 py-1.5"
                    />
                    <textarea
                      value={d.description || ''}
                      onChange={(e) => updateDraft(index, { description: e.target.value })}
                      placeholder="Description"
                      rows={2}
                      className="w-full text-sm rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary px-2 py-1.5"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="text-xs text-bw-muted">Type
                        <select
                          value={DELIVERABLE_TYPES.includes((d.type || '').toLowerCase()) ? (d.type || '').toLowerCase() : 'other'}
                          onChange={(e) => updateDraft(index, { type: e.target.value })}
                          className="ml-1 text-sm rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary py-1"
                        >
                          {DELIVERABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className="text-xs text-bw-muted">Hours
                        <input
                          type="number" min={0}
                          value={d.total_hours?.recommended ?? 0}
                          onChange={(e) => updateDraftHours(index, 'recommended', parseInt(e.target.value) || 0)}
                          className="ml-1 w-20 text-sm rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary py-1"
                        />
                      </label>
                      <label className="text-xs text-bw-muted">Min
                        <input
                          type="number" min={0}
                          value={d.total_hours?.min ?? 0}
                          onChange={(e) => updateDraftHours(index, 'min', parseInt(e.target.value) || 0)}
                          className="ml-1 w-16 text-sm rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary py-1"
                        />
                      </label>
                      <label className="text-xs text-bw-muted">Max
                        <input
                          type="number" min={0}
                          value={d.total_hours?.max ?? 0}
                          onChange={(e) => updateDraftHours(index, 'max', parseInt(e.target.value) || 0)}
                          className="ml-1 w-16 text-sm rounded-md border-bw-border focus:border-bw-primary focus:ring-bw-primary py-1"
                        />
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={() => removeDraftDeliverable(index)}
                    className="text-urgent hover:bg-urgent-bg rounded-md p-1.5 shrink-0"
                    title="Remove deliverable"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={addDraftDeliverable}
              className="w-full py-2 text-sm text-bw-primary border border-dashed border-bw-border rounded-lg hover:bg-bw-primary-tint flex items-center justify-center gap-1"
            >
              <Plus className="h-4 w-4" /> Add deliverable
            </button>
            <p className="text-xs text-bw-muted">
              Editing {draft.length} deliverable(s). Totals and the labor-category table recompute on save.
            </p>
          </div>
        )}

        {expandedSections.has('deliverables') && !editing && (
          <div className="space-y-3">
            {visibleDeliverables.map((deliverable, index) => (
              <DeliverableCard
                key={deliverable.deliverable_id}
                deliverable={deliverable}
                index={index}
                onEnhance={
                  onEnhanceSection
                    ? () => onEnhanceSection(deliverable.deliverable_id)
                    : undefined
                }
                isEnhancing={isEnhancing && enhancingId === deliverable.deliverable_id}
              />
            ))}

            {hasMoreDeliverables && (
              <button
                onClick={() => setShowAllDeliverables(!showAllDeliverables)}
                className="w-full py-2 text-sm text-bw-primary hover:text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors flex items-center justify-center gap-1"
              >
                {showAllDeliverables ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show All ({deliverables_with_labor.length - 5} more)
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Labor Summary Table */}
      {labor_summary && labor_summary.by_category && labor_summary.by_category.length > 0 && (
        <LaborSummaryTable laborSummary={labor_summary} />
      )}

      {/* AI-Derived Pricing + clarifying questions */}
      {effectiveAiPricing && (
        <AiPricingPanel
          aiPricing={effectiveAiPricing}
          onReprice={handleReprice}
          isRepricing={isRepricing}
        />
      )}

      {/* Compliance Checklist */}
      {compliance_framework && <ComplianceChecklist compliance={compliance_framework} />}

      {/* Pricing Inputs Preview */}
      {pricing_inputs && pricing_inputs.labor_categories_required && pricing_inputs.labor_categories_required.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('pricing')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 hover:to-slate-50 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-success" />
              Pricing Inputs
            </h3>
            {expandedSections.has('pricing') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>

          {expandedSections.has('pricing') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <div>
                <h4 className="text-sm font-medium text-bw-ink mb-2">
                  Required Labor Categories
                </h4>
                <div className="flex flex-wrap gap-2">
                  {pricing_inputs.labor_categories_required.map((cat, i) => (
                    <span
                      key={i}
                      className="inline-flex px-2 py-1 bg-success-bg text-success rounded text-sm"
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-bw-surface-subtle rounded-lg p-3">
                  <span className="font-medium text-bw-ink">Suggested Rate Basis:</span>
                  <span className="ml-2 text-bw-ink uppercase">
                    {pricing_inputs.suggested_rate_basis}
                  </span>
                </div>
                <div className="bg-bw-surface-subtle rounded-lg p-3">
                  <span className="font-medium text-bw-ink">SCA Wage Determination:</span>
                  <span
                    className={`ml-2 ${
                      pricing_inputs.sca_wage_determination_needed
                        ? 'text-fit-moderate'
                        : 'text-bw-muted'
                    }`}
                  >
                    {pricing_inputs.sca_wage_determination_needed ? 'Required' : 'Not Required'}
                  </span>
                </div>
              </div>

              <p className="text-xs text-bw-muted">
                These inputs will be used to generate accurate pricing in the Pricing tab.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeliverablesSectionContent;
